import React, { useCallback, useEffect, useState } from 'react';
import { CenterFrameLayout } from 'Layouts';
import { addBangoSubscription } from 'Api/AccountApi';
import { getEntitlements } from 'State/User';
import { encodeSearchParams } from 'Utils/Path';
import { StaticLinkButton } from 'Components/Fields/Buttons';
import { useStore } from 'Hooks/useStore';
import './BangoFinal.css';

export const BangoFinal = () => {
  const {
    routes,
    defaultConfig: { rootDomain },
  } = useStore('appConfig');
  const { subscriptionId, redirectUrl } = useStore('integrations.bango');
  const [view, setView] = useState('LOADING'); // 'LOADING' 'SUCCESS' 'INELIGIBLE' 'ERROR'
  const [addBangoSuccess, setAddBangoSuccess] = useState(false);

  const { isStatusSuccess, hasActiveSubscription, isPendingCancel, purchaseType } = useStore('subscription');

  //Including rootDomain so reloads site and pulls params again after sign out
  //URL comes in encoded from Verizon Bango, so encoding it again.
  const returnToSearchParams = encodeSearchParams({ subscriptionRequestId: subscriptionId, redirect_uri: redirectUrl });
  const returnToUrl = `${rootDomain}${routes.signupCreateAccount}?${returnToSearchParams}`;
  const searchParams = encodeSearchParams({ return_to: returnToUrl });
  const createAccountUrl = `${routes.signout}?${searchParams}`;

  const text = {
    LOADING: {
      SHOW_LOADER: true,
      TITLE: 'Checking your subscription',
    },
    SUCCESS: {
      TITLE: 'Congratulations!',
      BODY: 'Your account has been successfully activated with Verizon +play subscription. Enjoy Fox Nation!',
      PRIMARY_BUTTON: 'Continue to Verizon',
      PRIMARY_PATH: redirectUrl,
    },
    INELIGIBLE: {
      TITLE: 'Good News!',
      BODY: 'It looks like you are already a valued Fox Nation subscriber. If you would like to purchase an additional subscription through Verizon +play, please use a new email address.',
      PRIMARY_BUTTON: 'Yes, take me back to Create Account',
      PRIMARY_PATH: createAccountUrl,
      SECONDARY_BUTTON: 'No, take me back to Verizon +play',
      SECONDARY_PATH: redirectUrl,
    },
    ERROR: {
      TITLE: 'Something went wrong',
      BODY: 'Please re-start the activation process',
      PRIMARY_BUTTON: 'Back to Verizon',
      PRIMARY_PATH: redirectUrl,
    },
  };

  const addBango = useCallback(async () => {
    const { isSuccess } = await addBangoSubscription(subscriptionId);
    if (isSuccess) {
      setAddBangoSuccess(true);
      // Getting entitlements shouldn't be necessary since user will be going to Verizon after sign up
      // Doing it just to be extra safe
      getEntitlements();
    } else {
      setView('ERROR');
    }
  }, [subscriptionId]);

  useEffect(() => {
    if (isStatusSuccess) {
      const hasBangoSub = addBangoSuccess || purchaseType === 'bango';
      if (hasBangoSub) {
        setView('SUCCESS');
      } else if (hasActiveSubscription || isPendingCancel) {
        setView('INELIGIBLE');
      } else if (subscriptionId && !hasBangoSub) {
        addBango();
      } else {
        setView('ERROR');
      }
    }
  }, [
    isStatusSuccess,
    hasActiveSubscription,
    subscriptionId,
    purchaseType,
    addBangoSuccess,
    isPendingCancel,
    addBango,
  ]);

  return (
    <CenterFrameLayout title={text[view].TITLE} showLoader={text[view].SHOW_LOADER} modernLayout>
      <div>
        <div className="bangoMainText">{text[view].BODY}</div>
        {!!text[view].PRIMARY_BUTTON && !!text[view].PRIMARY_PATH && (
          <StaticLinkButton
            className="bangoPrimaryButton"
            path={text[view].PRIMARY_PATH}
            text={text[view].PRIMARY_BUTTON}
          />
        )}
        {!!text[view].SECONDARY_BUTTON && !!text[view].SECONDARY_PATH && (
          <a className="bangoSecondaryButton" href={text[view].SECONDARY_PATH}>
            {text[view].SECONDARY_BUTTON}
          </a>
        )}
      </div>
    </CenterFrameLayout>
  );
};
